/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
/* eslint-disable jsx-a11y/anchor-has-content */
import { FloatingPortal } from '@floating-ui/react'
import { EditPencil } from 'iconoir-react'
import FloatingSave from 'app/components/FloatingSave'
import { Info } from 'app/components/Info'
import { SchedulingFormFieldsCard } from 'app/components/scheduling/SchedulingFormFieldsCard'
import LeadOverridePanel from 'app/components/scheduling/LeadOverridePanel'
import { LocationLogoUploader } from 'app/components/scheduling/LocationLogoUploader'
import { SectionCard } from 'app/components/SectionCard'
import { Toggle } from 'app/components/Toggle'
import type { UseSchedulingSettingsReturn } from './useSchedulingSettings'

export type SchedulingSettingsComponentProps = UseSchedulingSettingsReturn

export function SchedulingSettingsComponent(props: SchedulingSettingsComponentProps) {
  const {
    schedulingConfiguration,
    setSchedulingConfiguration,
    customQuestions,
    showQuestionPanel,
    setShowQuestionPanel,
    showLeadOverride,
    setShowLeadOverride,
    isDirtyForm,
    setIsDirtyForm,
    locationId,
    saveRequest,
    floatingData,
    getReferenceProps,
    getFloatingProps,
    floatingDataLeadOverride,
    getLeadOverrideReferenceProps,
    getLeadOverrideFloatingProps,
    handleSaveConfiguration,
    onMoveQuestion,
    onDropQuestion,
    onQuestionCreateCompleted,
    onQuestionUpdateCompleted,
    onQuestionDeleted,
    onLeadOverrideSaved,
    onLeadOverrideToggle,
  } = props

  return (
    <>
      <div className="mt-4 flex max-w-[750px] flex-col gap-8">
        {/* General Settings */}
        <SectionCard
          title="General Settings"
          headerButtons={
            <div className="flex items-center gap-2">
              <span className="text-label-sm text-strong">Lead Capture Override</span>
              <Info
                title="New Patient Lead Capture Override"
                message="Prevents a potential patient from seeing the calendar if there is no availability within the designated time. The override will instead show an appointment request form. This is designed to improve the conversion rate and give you the highest chance of capturing a new lead."
                className="text-strong"
              />
              <Toggle
                defaultValue={!!schedulingConfiguration?.leadOverrideEnabled}
                handleToggle={(val) => onLeadOverrideToggle(val)}
              />
            </div>
          }
        >
          <div>
            <p className="mb-2 text-label-sm text-strong">Lead Capture Override</p>
            <p className="mb-4 text-body-sm text-minimal">Replace Calendar with Form if no appointments within...</p>
            <button
              className="flex w-full items-center justify-between rounded-lg border border-default bg-surface px-3 py-2.5 text-strong disabled:bg-surface-strong disabled:text-subtle"
              disabled={!schedulingConfiguration?.leadOverrideEnabled}
              ref={floatingDataLeadOverride.refs.setReference}
              {...getLeadOverrideReferenceProps()}
            >
              <div className="flex items-center gap-2">
                <span className="text-label-sm">{schedulingConfiguration?.leadOverrideConfig?.amount}</span>
                <span className="text-label-sm">{schedulingConfiguration?.leadOverrideConfig?.unit}</span>
              </div>
              <EditPencil height={20} />
            </button>
            {showLeadOverride && (
              <FloatingPortal>
                <div
                  className="rounded-lg border border-default bg-surface shadow-elevation-2"
                  ref={floatingDataLeadOverride.refs.setFloating}
                  {...getLeadOverrideFloatingProps()}
                  style={{ ...floatingDataLeadOverride.floatingStyles }}
                >
                  <LeadOverridePanel
                    config={schedulingConfiguration?.leadOverrideConfig}
                    onSaved={onLeadOverrideSaved}
                    onCancel={() => setShowLeadOverride(false)}
                  />
                </div>
              </FloatingPortal>
            )}
          </div>
        </SectionCard>

        {/* Branding */}
        <SectionCard title="Branding">
          <div>
            <p className="text-ui-md text-strong">Online Scheduling</p>
            <p className="mb-6 mt-2 text-body-sm text-strong">
              This logo will be used in the online scheduler. If no logo is provided, the location name will be shown
              instead.
            </p>
            <LocationLogoUploader locationId={locationId!} />
            <p className="mt-4 text-body-xs text-minimal">Recommended Dimensions: 300x100 pixels</p>
          </div>
        </SectionCard>

        {/* Form Fields */}
        <SectionCard title="Form Fields">
          <SchedulingFormFieldsCard {...props} />
        </SectionCard>
      </div>
      <FloatingSave isDirtyForm={isDirtyForm} handleSaveConfiguration={handleSaveConfiguration} />
    </>
  )
}
